import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';

const Hero = ({ onOpenContact }) => {
  const { t, currentLang } = useLanguage();
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Video caption translation
  const getVideoCaption = () => {
    const captions = {
      en: 'Watch: How Hübner Capital works',
      it: 'Guarda: Come funziona Hübner Capital',
      'es-es': 'Ver: Cómo funciona Hübner Capital',
      'es-latam': 'Ver: Cómo funciona Hübner Capital'
    };
    return captions[currentLang] || captions.en;
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-white"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.03]">
          <svg viewBox="0 0 400 400" className="w-full h-full">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1A3A4A" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Badge - Institutional styling */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#1A3A4A]/[0.04] mb-5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#60B5AF]" />
              <span className="text-xs font-body text-[#1A3A4A]/70 font-medium tracking-wide uppercase">{t('hero.badge')}</span>
            </div>

            {/* Headline */}
            <h1 
              data-testid="hero-headline"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A3A4A] tracking-tight mb-2 leading-tight"
            >
              {t('hero.headline')}
            </h1>
            <p className="font-heading text-xl sm:text-2xl lg:text-3xl font-semibold text-[#60B5AF] tracking-tight mb-6">
              {t('hero.headlineAccent')}
            </p>

            {/* Subheadline */}
            <p 
              data-testid="hero-subhead"
              className="font-body text-lg text-[#1A3A4A]/70 max-w-xl mb-3 leading-relaxed"
            >
              {t('hero.subhead')}
            </p>

            {/* Supporting line */}
            <p 
              data-testid="hero-supporting"
              className="font-body text-base text-[#1A3A4A]/50 max-w-xl mb-8 leading-relaxed"
            >
              {t('hero.supportingLine')}
            </p>

            {/* CTAs - Institutional styling with reduced border radius */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-cta-primary"
                onClick={onOpenContact}
                className="bg-[#1A3A4A] text-white hover:bg-[#1A3A4A]/90 font-body font-medium rounded-md px-8 py-6 text-base shadow-lg"
              >
                {t('hero.cta1')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                onClick={() => scrollToSection('services')}
                variant="outline"
                className="border-[#1A3A4A]/20 text-[#1A3A4A] hover:bg-[#1A3A4A]/5 hover:border-[#1A3A4A]/40 font-body font-medium rounded-md px-8 py-6 text-base"
              >
                {t('hero.cta2')}
              </Button>
            </div>
            
            {/* CTA Subtext */}
            <p className="font-body text-sm text-[#1A3A4A]/40 mt-4">
              {t('hero.ctaSubtext')}
            </p>

            {/* Trust Bullets - Teal for checkmarks */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mt-8 pt-6 border-t border-[#1A3A4A]/10">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#60B5AF] flex-shrink-0" />
                <span className="font-body text-sm text-[#1A3A4A]/70">{t('hero.trustBullet1')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#60B5AF] flex-shrink-0" />
                <span className="font-body text-sm text-[#1A3A4A]/70">{t('hero.trustBullet2')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#60B5AF] flex-shrink-0" />
                <span className="font-body text-sm text-[#1A3A4A]/70">{t('hero.trustBullet3')}</span>
              </div>
            </div>
          </motion.div>

          {/* Right - Clean Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col"
          >
            {/* Video Container - Custom Brand Video with institutional border radius */}
            <div 
              id="hero-video-container"
              className="relative rounded-lg overflow-hidden shadow-2xl cursor-pointer group"
              style={{ aspectRatio: '16/9' }}
              onClick={toggleMute}
            >
              {/* Custom HÜBNER Brand Video */}
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={`${process.env.REACT_APP_BACKEND_URL}/api/static/video_assets/video_poster.jpg`}
                data-testid="hero-video"
              >
                <source 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/static/video_assets/hubner_brand_video.mp4`}
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
              
              {/* Subtle Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A4A]/20 via-transparent to-transparent pointer-events-none" />
              
              {/* Mute/Unmute Button Overlay */}
              <div className="absolute bottom-4 right-4 z-10">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                  className="p-3 rounded-full bg-[#1A3A4A]/70 hover:bg-[#1A3A4A]/90 transition-all text-white backdrop-blur-sm"
                  data-testid="video-mute-toggle"
                  aria-label={isMuted ? "Enable audio" : "Mute audio"}
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Click to unmute hint (shows only when muted) */}
              {isMuted && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="px-4 py-2 rounded-full bg-[#1A3A4A]/70 text-white text-sm font-body backdrop-blur-sm">
                    {currentLang?.startsWith('es') ? 'Click para activar audio' : 'Click to enable audio'}
                  </div>
                </div>
              )}
            </div>

            {/* Video Caption - Centered below */}
            <p className="text-center font-body text-sm text-[#1A3A4A]/40 italic mt-4">
              {getVideoCaption()}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
